import { useState, useRef, useEffect, useCallback } from 'react';
import {
	Select,
	Input,
	InputNumber,
	Button,
	Card,
	message,
	Form,
	Upload,
	Switch,
	Spin,
	Tag,
	Empty,
} from 'antd';
import {
	UploadOutlined,
	DeleteOutlined,
	SearchOutlined,
	SettingOutlined,
	DownloadOutlined,
	SaveOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import { gql, useQuery } from '@apollo/client';
import { uploadToS3 } from 'src/_shared/services/utils';
import {
	fetchS3Bytes,
	scanPdfFields,
	generateFromFieldOverrides,
} from './certificatePdfUtils';

const S3_BUCKET = 'erin-documents';

const LIST_CERTIFICATE_ASSETS = gql`
	query ListCertificateAssets($companyId: ID!) {
		listCertificateAssets(companyId: $companyId) {
			templates {
				key
				fileName
				size
			}
			fonts {
				key
				fileName
			}
			configs {
				key
				fileName
			}
		}
	}
`;

function FieldCard({
	field,
	tagColor,
	enabledOverrides,
	setEnabledOverrides,
	overrides,
	setOverrides,
	fieldStyles,
	setFieldStyles,
	fontOptions,
	variableNames,
	setVariableNames,
}) {
	const isEnabled = !!enabledOverrides[field.name];
	return (
		<Card
			key={field.name}
			size="small"
			style={{ marginBottom: 8 }}
			bodyStyle={{ padding: '8px 12px' }}
		>
			<div
				style={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					marginBottom: 6,
				}}
			>
				<div style={{ flex: 1, minWidth: 0 }}>
					{tagColor ? (
						<>
							<strong>{field.name}</strong>
							<Tag color={tagColor} style={{ marginLeft: 8 }}>
								{field.type}
							</Tag>
						</>
					) : (
						<>
							<Tag>{field.type}</Tag>
							<span style={{ fontSize: 12, color: '#555' }}>{field.name}</span>
						</>
					)}
				</div>
				<Switch
					size="small"
					checked={isEnabled}
					onChange={(checked) =>
						setEnabledOverrides((prev) => ({
							...prev,
							[field.name]: checked,
						}))
					}
				/>
			</div>
			<div
				style={{
					fontSize: 12,
					color: '#595959',
					marginBottom: isEnabled ? 6 : 0,
					wordBreak: 'break-all',
				}}
			>
				Current: &quot;{field.value || '(empty)'}&quot;
			</div>
			{isEnabled && (
				<>
					<Input
						size="small"
						placeholder="Variable name (for config)"
						value={variableNames[field.name] ?? ''}
						onChange={(e) =>
							setVariableNames((prev) => ({
								...prev,
								[field.name]: e.target.value,
							}))
						}
						style={{ marginBottom: 4 }}
					/>
					<Input
						size="small"
						placeholder="Replacement value"
						value={overrides[field.name] ?? ''}
						onChange={(e) =>
							setOverrides((prev) => ({
								...prev,
								[field.name]: e.target.value,
							}))
						}
					/>
					<FieldStyleControls
						fieldName={field.name}
						fieldStyles={fieldStyles}
						setFieldStyles={setFieldStyles}
						fontOptions={fontOptions}
					/>
				</>
			)}
		</Card>
	);
}

function FieldStyleControls({
	fieldName,
	fieldStyles,
	setFieldStyles,
	fontOptions = [],
}) {
	const styles = fieldStyles[fieldName] || {};
	const [expanded, setExpanded] = useState(false);
	const [loadingFont, setLoadingFont] = useState(false);

	const updateStyle = (key, value) => {
		setFieldStyles((prev) => ({
			...prev,
			[fieldName]: { ...prev[fieldName], [key]: value },
		}));
	};

	const handleFontUpload = (file) => {
		const reader = new FileReader();
		reader.addEventListener('load', () => {
			updateStyle('fontBytes', new Uint8Array(reader.result));
			updateStyle('fontName', file.name);
		});
		reader.readAsArrayBuffer(file);
		return false;
	};

	const handleS3FontSelect = async (s3Key) => {
		if (!s3Key) {
			updateStyle('fontBytes', undefined);
			updateStyle('fontName', undefined);
			updateStyle('fontKey', undefined);
			return;
		}

		setLoadingFont(true);
		try {
			const fontBytes = await fetchS3Bytes(s3Key);
			const font = fontOptions.find((f) => f.value === s3Key);
			updateStyle('fontBytes', fontBytes);
			updateStyle('fontName', font?.label || s3Key);
			updateStyle('fontKey', s3Key);
		} catch {
			message.error('Failed to load font');
		} finally {
			setLoadingFont(false);
		}
	};

	return (
		<div style={{ marginTop: 6 }}>
			<Button
				type="link"
				size="small"
				icon={<SettingOutlined />}
				onClick={() => setExpanded(!expanded)}
				style={{ padding: 0, fontSize: 12 }}
			>
				{expanded ? 'Hide styling' : 'Styling options'}
			</Button>
			{expanded && (
				<div
					style={{
						marginTop: 6,
						padding: 8,
						background: '#fafafa',
						borderRadius: 6,
						display: 'flex',
						flexDirection: 'column',
						gap: 6,
					}}
				>
					<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
						<span style={{ fontSize: 12, color: '#666', minWidth: 40 }}>
							Font:
						</span>
						{styles.fontName ? (
							<div
								style={{
									display: 'flex',
									alignItems: 'center',
									gap: 6,
									flex: 1,
								}}
							>
								<span style={{ fontSize: 12 }}>{styles.fontName}</span>
								<Button
									size="small"
									danger
									icon={<DeleteOutlined />}
									onClick={() => {
										updateStyle('fontBytes', undefined);
										updateStyle('fontName', undefined);
										updateStyle('fontKey', undefined);
									}}
								/>
							</div>
						) : (
							<div
								style={{
									display: 'flex',
									gap: 6,
									flex: 1,
									alignItems: 'center',
								}}
							>
								{fontOptions.length > 0 && (
									<Select
										size="small"
										placeholder="Select font"
										options={fontOptions}
										onChange={handleS3FontSelect}
										loading={loadingFont}
										allowClear
										style={{ flex: 1 }}
									/>
								)}
								<Upload
									accept=".otf,.ttf,.woff"
									showUploadList={false}
									beforeUpload={handleFontUpload}
								>
									<Button size="small" icon={<UploadOutlined />}>
										Upload
									</Button>
								</Upload>
							</div>
						)}
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
						<span style={{ fontSize: 12, color: '#666', minWidth: 40 }}>
							Size:
						</span>
						<InputNumber
							size="small"
							min={1}
							max={200}
							placeholder="pt (default)"
							value={styles.fontSize ?? null}
							onChange={(val) => updateStyle('fontSize', val)}
							style={{ width: 130 }}
						/>
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
						<span style={{ fontSize: 12, color: '#666', minWidth: 40 }}>
							Color:
						</span>
						<Input
							size="small"
							placeholder="#000000 (default)"
							value={styles.colorHex ?? ''}
							onChange={(e) => updateStyle('colorHex', e.target.value)}
							style={{ flex: 1 }}
							prefix={
								styles.colorHex && /^#[\dA-Fa-f]{6}$/.test(styles.colorHex) ? (
									<span
										style={{
											display: 'inline-block',
											width: 12,
											height: 12,
											borderRadius: 2,
											backgroundColor: styles.colorHex,
											border: '1px solid #d9d9d9',
										}}
									/>
								) : null
							}
						/>
					</div>
					<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
						<span style={{ fontSize: 12, color: '#666', minWidth: 40 }}>
							Center:
						</span>
						<Switch
							size="small"
							checked={!!styles.centerHorizontally}
							onChange={(checked) => updateStyle('centerHorizontally', checked)}
						/>
						<span style={{ fontSize: 11, color: '#595959' }}>
							Center text on page
						</span>
					</div>
					{!styles.centerHorizontally && (
						<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
							<span style={{ fontSize: 12, color: '#666', minWidth: 40 }}>
								X offset:
							</span>
							<InputNumber
								size="small"
								placeholder="0"
								value={styles.offsetX ?? null}
								onChange={(val) => updateStyle('offsetX', val)}
								style={{ width: 130 }}
							/>
						</div>
					)}
					<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
						<span style={{ fontSize: 12, color: '#666', minWidth: 40 }}>
							Y offset:
						</span>
						<InputNumber
							size="small"
							placeholder="0"
							value={styles.offsetY ?? null}
							onChange={(val) => updateStyle('offsetY', val)}
							style={{ width: 130 }}
						/>
					</div>
				</div>
			)}
		</div>
	);
}

function FieldInspectorTab({
	templateOptions,
	fontOptions,
	loadingAssets,
	companyId,
	refetchAssets,
	configKeys,
}) {
	const [selectedCert, setSelectedCert] = useState(null);
	const [scanning, setScanning] = useState(false);
	const [generating, setGenerating] = useState(false);
	const [scannedFields, setScannedFields] = useState([]);
	const [overrides, setOverrides] = useState({});
	const [enabledOverrides, setEnabledOverrides] = useState({});
	const [fieldStyles, setFieldStyles] = useState({});
	const [variableNames, setVariableNames] = useState({});
	const [configName, setConfigName] = useState('');
	const [selectedConfigKey, setSelectedConfigKey] = useState(null);
	const [configOptions, setConfigOptions] = useState([]);
	const [loadingConfigs, setLoadingConfigs] = useState(false);
	const [loadingConfig, setLoadingConfig] = useState(false);
	const [previewUrl, setPreviewUrl] = useState(null);
	const [previewing, setPreviewing] = useState(false);
	const previewUrlRef = useRef(null);
	const debounceRef = useRef(null);
	const previewGenRef = useRef(0);
	const templateCacheRef = useRef({ key: null, bytes: null });

	const getCachedPdfBytes = useCallback(async (certKey) => {
		if (templateCacheRef.current.key === certKey) {
			return templateCacheRef.current.bytes;
		}

		const bytes = await fetchS3Bytes(certKey);
		templateCacheRef.current = { key: certKey, bytes };
		return bytes;
	}, []);

	const revokePreview = useCallback(() => {
		if (previewUrlRef.current) {
			URL.revokeObjectURL(previewUrlRef.current);
			previewUrlRef.current = null;
		}
	}, []);

	// Cleanup on unmount
	useEffect(() => {
		return () => {
			revokePreview();
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [revokePreview]);

	const updatePreview = useCallback(
		async (
			certKey,
			currentOverrides,
			currentEnabled,
			currentFields,
			currentStyles
		) => {
			if (!certKey) return;

			const gen = ++previewGenRef.current;

			const activeOverrides = {};
			for (const [key, value] of Object.entries(currentOverrides)) {
				if (currentEnabled[key]) {
					activeOverrides[key] = value;
				}
			}

			setPreviewing(true);
			try {
				const pdfBytes = await getCachedPdfBytes(certKey);

				// Discard stale result if a newer preview was requested
				if (gen !== previewGenRef.current) return;

				const hasOverrides = Object.keys(activeOverrides).length > 0;
				const modifiedPdf = hasOverrides
					? await generateFromFieldOverrides(
							pdfBytes,
							activeOverrides,
							currentFields,
							currentStyles
						)
					: pdfBytes;

				// Discard stale result if a newer preview was requested
				if (gen !== previewGenRef.current) return;

				revokePreview();
				const blob = new Blob([modifiedPdf], { type: 'application/pdf' });
				const url = URL.createObjectURL(blob);
				previewUrlRef.current = url;
				setPreviewUrl(url);
			} catch (error) {
				if (gen === previewGenRef.current) {
					console.error('Preview failed:', error);
				}
			} finally {
				if (gen === previewGenRef.current) {
					setPreviewing(false);
				}
			}
		},
		[revokePreview, getCachedPdfBytes]
	);

	// Show raw preview immediately when template changes
	useEffect(() => {
		if (selectedCert) {
			templateCacheRef.current = { key: null, bytes: null };
			updatePreview(selectedCert, {}, {}, [], {});
		} else {
			revokePreview();
			setPreviewUrl(null);
		}
	}, [selectedCert, updatePreview, revokePreview]);

	// Debounced auto-preview when overrides/styles change
	useEffect(() => {
		if (!selectedCert || scannedFields.length === 0) return;
		const hasActiveOverrides = Object.keys(enabledOverrides).some(
			(k) => enabledOverrides[k]
		);
		if (!hasActiveOverrides) return;
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			updatePreview(
				selectedCert,
				overrides,
				enabledOverrides,
				scannedFields,
				fieldStyles
			);
		}, 600);
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
	}, [
		selectedCert,
		overrides,
		enabledOverrides,
		fieldStyles,
		scannedFields,
		updatePreview,
	]);

	// Fetch config names for dropdown
	const configKeysJson = JSON.stringify(
		(configKeys || []).map((c) => c.key)
	);
	useEffect(() => {
		const keys = JSON.parse(configKeysJson);
		if (keys.length === 0) {
			setConfigOptions([]);
			return;
		}

		let cancelled = false;
		setLoadingConfigs(true);
		Promise.all(
			keys.map(async (key) => {
				try {
					const bytes = await fetchS3Bytes(key);
					const json = JSON.parse(new TextDecoder().decode(bytes));
					return { label: json.name || key, value: key };
				} catch {
					return { label: key, value: key };
				}
			})
		).then((options) => {
			if (!cancelled) {
				setConfigOptions(
					options.sort((a, b) => a.label.localeCompare(b.label))
				);
				setLoadingConfigs(false);
			}
		});

		return () => {
			cancelled = true;
		};
	}, [configKeysJson]);

	const handleLoadConfig = async (configKey) => {
		if (!configKey) return;

		setLoadingConfig(true);
		try {
			// Fetch and parse config
			const configBytes = await fetchS3Bytes(configKey);
			const config = JSON.parse(new TextDecoder().decode(configBytes));

			// Set config metadata
			setConfigName(config.name || '');

			// Set template
			setSelectedCert(config.templateKey);
			templateCacheRef.current = { key: null, bytes: null };

			// Fetch template and scan
			const pdfBytes = await fetchS3Bytes(config.templateKey);
			templateCacheRef.current = {
				key: config.templateKey,
				bytes: pdfBytes,
			};
			const fields = await scanPdfFields(pdfBytes);
			setScannedFields(fields);

			// Collect unique font keys to fetch
			const fontKeysToFetch = new Set();
			for (const f of config.fields) {
				if (f.style?.font) fontKeysToFetch.add(f.style.font);
			}

			// Fetch all fonts in parallel
			const fontBytesMap = {};
			await Promise.all(
				[...fontKeysToFetch].map(async (fontKey) => {
					try {
						fontBytesMap[fontKey] = await fetchS3Bytes(fontKey);
					} catch {
						// Font might have been removed
					}
				})
			);

			// Apply config field settings
			const newEnabled = {};
			const newVarNames = {};
			const newStyles = {};

			for (const configField of config.fields) {
				newEnabled[configField.fieldName] = true;
				newVarNames[configField.fieldName] = configField.variableName;

				if (configField.style) {
					const style = {};
					if (configField.style.font) {
						style.fontKey = configField.style.font;
						const fontOpt = fontOptions.find(
							(f) => f.value === configField.style.font
						);
						style.fontName = fontOpt?.label || configField.style.font;
						if (fontBytesMap[configField.style.font]) {
							style.fontBytes =
								fontBytesMap[configField.style.font];
						}
					}

					if (configField.style.fontSize != null)
						style.fontSize = configField.style.fontSize;
					if (configField.style.color)
						style.colorHex = configField.style.color;
					if (configField.style.offsetX != null)
						style.offsetX = configField.style.offsetX;
					if (configField.style.offsetY != null)
						style.offsetY = configField.style.offsetY;
					if (configField.style.centerHorizontally)
						style.centerHorizontally = true;
					if (Object.keys(style).length > 0) {
						newStyles[configField.fieldName] = style;
					}
				}
			}

			setEnabledOverrides(newEnabled);
			setVariableNames(newVarNames);
			setOverrides({});
			setFieldStyles(newStyles);

			message.success(`Config "${config.name}" loaded`);
		} catch (error) {
			console.error('Failed to load config:', error);
			message.error('Failed to load config');
		} finally {
			setLoadingConfig(false);
		}
	};

	const handleScan = async () => {
		if (!selectedCert) {
			message.error('Select a template first');
			return;
		}

		setScanning(true);
		setScannedFields([]);
		setOverrides({});
		setEnabledOverrides({});
		setFieldStyles({});
		setVariableNames({});
		try {
			const pdfBytes = await getCachedPdfBytes(selectedCert);
			const fields = await scanPdfFields(pdfBytes);
			setScannedFields(fields);
			if (fields.length === 0) {
				message.info('No text fields found in this PDF');
			}
		} catch (error) {
			console.error('Scan failed:', error);
			message.error('Failed to scan PDF');
		} finally {
			setScanning(false);
		}
	};

	const handleGenerate = async () => {
		if (!selectedCert) return;

		const activeOverrides = {};
		for (const [key, value] of Object.entries(overrides)) {
			if (enabledOverrides[key]) {
				activeOverrides[key] = value;
			}
		}

		if (Object.keys(activeOverrides).length === 0) {
			message.warning('Enable at least one field override');
			return;
		}

		setGenerating(true);
		try {
			const pdfBytes = await getCachedPdfBytes(selectedCert);
			const modifiedPdf = await generateFromFieldOverrides(
				pdfBytes,
				activeOverrides,
				scannedFields,
				fieldStyles
			);

			const blob = new Blob([modifiedPdf], { type: 'application/pdf' });
			const url = URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.download = 'certificate-inspector.pdf';
			document.body.append(link);
			link.click();
			link.remove();
			URL.revokeObjectURL(url);
			message.success('Certificate generated');
		} catch (error) {
			console.error('Generation failed:', error);
			message.error('Failed to generate certificate');
		} finally {
			setGenerating(false);
		}
	};

	const MAX_TEMPLATE_SIZE = 25 * 1024 * 1024; // 25 MB
	const MAX_FONT_SIZE = 10 * 1024 * 1024; // 10 MB

	const handleUploadTemplate = async (file) => {
		if (file.size > MAX_TEMPLATE_SIZE) {
			message.error('Template must be under 25 MB');
			return;
		}

		const safeName = file.name.replaceAll(/[^a-zA-Z0-9._-]/g, '_');
		const key = `certificates/${companyId}/templates/${safeName}`;
		try {
			await uploadToS3(file, key, S3_BUCKET);
			message.success(`Template "${file.name}" uploaded`);
			refetchAssets();
		} catch {
			message.error('Failed to upload template');
		}
	};

	const handleUploadFont = async (file) => {
		if (file.size > MAX_FONT_SIZE) {
			message.error('Font must be under 10 MB');
			return;
		}

		const safeName = file.name.replaceAll(/[^a-zA-Z0-9._-]/g, '_');
		const key = `certificates/${companyId}/fonts/${safeName}`;
		try {
			await uploadToS3(file, key, S3_BUCKET);
			message.success(`Font "${file.name}" uploaded`);
			refetchAssets();
		} catch {
			message.error('Failed to upload font');
		}
	};

	const buildConfig = () => {
		const fields = [];
		for (const [fieldName, isEnabled] of Object.entries(enabledOverrides)) {
			if (!isEnabled) continue;
			const varName = variableNames[fieldName];
			if (!varName) continue;

			const field = scannedFields.find((f) => f.name === fieldName);
			if (!field) continue;

			const entry = {
				variableName: varName,
				fieldName: field.name,
				source: field.source,
			};

			const styles = fieldStyles[fieldName];
			if (styles) {
				const style = {};
				if (styles.fontKey) style.font = styles.fontKey;
				if (styles.fontSize != null) style.fontSize = styles.fontSize;
				if (styles.colorHex) style.color = styles.colorHex;
				if (styles.offsetX != null) style.offsetX = styles.offsetX;
				if (styles.offsetY != null) style.offsetY = styles.offsetY;
				if (styles.centerHorizontally) style.centerHorizontally = true;
				if (Object.keys(style).length > 0) {
					entry.style = style;
				}
			}

			fields.push(entry);
		}

		if (fields.length === 0) return null;

		return {
			id: crypto.randomUUID(),
			name: configName,
			templateKey: selectedCert,
			fields,
		};
	};

	const handleExportConfig = () => {
		if (!selectedCert) {
			message.error('Select a template first');
			return;
		}

		const config = buildConfig();
		if (!config) {
			message.warning('No fields with variable names to export');
			return;
		}

		const blob = new Blob([JSON.stringify(config, null, 2)], {
			type: 'application/json',
		});
		const url = URL.createObjectURL(blob);
		const link = document.createElement('a');
		link.href = url;
		link.download = `${config.id}.json`;
		document.body.append(link);
		link.click();
		link.remove();
		URL.revokeObjectURL(url);
		message.success('Configuration exported');
	};

	const handleUploadConfig = async () => {
		if (!selectedCert) {
			message.error('Select a template first');
			return;
		}

		if (!configName.trim()) {
			message.error('Enter a config name');
			return;
		}

		const config = buildConfig();
		if (!config) {
			message.warning('No fields with variable names to save');
			return;
		}

		const jsonString = JSON.stringify(config, null, 2);
		const blob = new Blob([jsonString], { type: 'application/json' });
		const file = new File([blob], `${config.id}.json`, {
			type: 'application/json',
		});
		const key = `certificates/${companyId}/configs/${config.id}.json`;
		try {
			await uploadToS3(file, key, S3_BUCKET);
			message.success(`Config "${configName}" saved`);
			refetchAssets();
		} catch {
			message.error('Failed to save config');
		}
	};

	const formFields = scannedFields.filter((f) => f.source === 'form');
	const annotFields = scannedFields.filter((f) => f.source === 'annotation');
	const streamFields = scannedFields.filter((f) => f.source === 'stream');

	return (
		<div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
			{/* Preview panel */}
			<Card
				size="small"
				title="Preview"
				extra={previewing ? <Spin size="small" /> : null}
				style={{ flex: 1, minWidth: 0 }}
				bodyStyle={{ padding: 0 }}
			>
				{previewUrl ? (
					<iframe
						src={previewUrl}
						style={{
							width: '100%',
							height: 700,
							border: 'none',
							display: 'block',
						}}
						title="Certificate Preview"
					/>
				) : (
					<Empty
						description="Select a template to preview"
						style={{ padding: 48 }}
					/>
				)}
			</Card>

			{/* Controls panel */}
			<Card
				size="small"
				title="Fields"
				style={{
					width: 380,
					flexShrink: 0,
					maxHeight: 'calc(100vh - 200px)',
					overflow: 'auto',
				}}
			>
				<Form layout="vertical">
					<Form.Item label="Saved Config">
						<Select
							placeholder={
								loadingConfigs
									? 'Loading configs...'
									: 'Load a saved config'
							}
							options={configOptions}
							loading={loadingConfigs || loadingConfig}
							disabled={loadingConfigs}
							notFoundContent={
								loadingConfigs ? (
									<Spin size="small" />
								) : (
									'No saved configs'
								)
							}
							onChange={(val) => {
								setSelectedConfigKey(val ?? null);
								if (val) handleLoadConfig(val);
							}}
							value={selectedConfigKey}
							allowClear
						/>
					</Form.Item>
					<Form.Item label="Certificate Template">
						<Select
							placeholder="Select a certificate"
							options={templateOptions}
							loading={loadingAssets}
							onChange={(val) => {
								setSelectedCert(val);
								setScannedFields([]);
								setOverrides({});
								setEnabledOverrides({});
								setFieldStyles({});
								setVariableNames({});
							}}
							value={selectedCert}
						/>
					</Form.Item>
					<Form.Item label="Config Name">
						<Input
							placeholder="e.g. Employee of the Month"
							value={configName}
							onChange={(e) => setConfigName(e.target.value)}
						/>
					</Form.Item>
					<Form.Item>
						<div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
							<Upload
								accept=".pdf"
								showUploadList={false}
								beforeUpload={(file) => {
									handleUploadTemplate(file);
									return false;
								}}
							>
								<Button size="small" icon={<UploadOutlined />}>
									Upload Template
								</Button>
							</Upload>
							<Upload
								accept=".otf,.ttf,.woff"
								showUploadList={false}
								beforeUpload={(file) => {
									handleUploadFont(file);
									return false;
								}}
							>
								<Button size="small" icon={<UploadOutlined />}>
									Upload Font
								</Button>
							</Upload>
							<Button
								size="small"
								icon={<DownloadOutlined />}
								onClick={handleExportConfig}
								disabled={
									!selectedCert ||
									!Object.values(enabledOverrides).some(Boolean)
								}
							>
								Export Config
							</Button>
							<Button
								size="small"
								icon={<SaveOutlined />}
								onClick={handleUploadConfig}
								disabled={
									!selectedCert ||
									!Object.values(enabledOverrides).some(Boolean)
								}
							>
								Upload Config
							</Button>
						</div>
					</Form.Item>
					<Form.Item>
						<Button
							icon={<SearchOutlined />}
							onClick={handleScan}
							loading={scanning}
							disabled={!selectedCert}
							block
						>
							Scan PDF Fields
						</Button>
					</Form.Item>

					{scanning && (
						<div style={{ textAlign: 'center', padding: 24 }}>
							<Spin tip="Scanning PDF..." />
						</div>
					)}

					{!scanning && scannedFields.length > 0 && (
						<>
							{formFields.length > 0 && (
								<>
									<h4 style={{ marginBottom: 12 }}>
										Form Fields ({formFields.length})
									</h4>
									{formFields.map((field) => (
										<FieldCard
											key={field.name}
											field={field}
											tagColor="blue"
											enabledOverrides={enabledOverrides}
											setEnabledOverrides={setEnabledOverrides}
											overrides={overrides}
											setOverrides={setOverrides}
											fieldStyles={fieldStyles}
											setFieldStyles={setFieldStyles}
											fontOptions={fontOptions}
											variableNames={variableNames}
											setVariableNames={setVariableNames}
										/>
									))}
								</>
							)}

							{annotFields.length > 0 && (
								<>
									<h4 style={{ margin: '16px 0 12px' }}>
										Annotations ({annotFields.length})
									</h4>
									{annotFields.map((field) => (
										<FieldCard
											key={field.name}
											field={field}
											tagColor="orange"
											enabledOverrides={enabledOverrides}
											setEnabledOverrides={setEnabledOverrides}
											overrides={overrides}
											setOverrides={setOverrides}
											fieldStyles={fieldStyles}
											setFieldStyles={setFieldStyles}
											fontOptions={fontOptions}
											variableNames={variableNames}
											setVariableNames={setVariableNames}
										/>
									))}
								</>
							)}

							{streamFields.length > 0 && (
								<>
									<h4 style={{ margin: '16px 0 12px' }}>
										Content Stream Text ({streamFields.length})
									</h4>
									{streamFields.map((field, i) => (
										<FieldCard
											key={`${field.name}-${i}`}
											field={field}
											enabledOverrides={enabledOverrides}
											setEnabledOverrides={setEnabledOverrides}
											overrides={overrides}
											setOverrides={setOverrides}
											fieldStyles={fieldStyles}
											setFieldStyles={setFieldStyles}
											fontOptions={fontOptions}
											variableNames={variableNames}
											setVariableNames={setVariableNames}
										/>
									))}
								</>
							)}

							{(formFields.length > 0 ||
								annotFields.length > 0 ||
								streamFields.length > 0) && (
								<Form.Item style={{ marginTop: 16 }}>
									<Button
										type="primary"
										onClick={handleGenerate}
										loading={generating}
										disabled={!Object.values(enabledOverrides).some(Boolean)}
										block
									>
										Download
									</Button>
								</Form.Item>
							)}
						</>
					)}

					{!scanning && scannedFields.length === 0 && selectedCert && (
						<Empty
							description="Click Scan to inspect fields"
							style={{ padding: 24 }}
						/>
					)}
				</Form>
			</Card>
		</div>
	);
}

function CertificateGeneratorComponent() {
	const currentUser = useSelector((state) => state.user.currentUser);
	const companyId = currentUser?.company?.id;

	const {
		data,
		loading: loadingAssets,
		refetch: refetchAssets,
	} = useQuery(LIST_CERTIFICATE_ASSETS, {
		variables: { companyId },
		skip: !companyId,
	});

	const templateOptions = (data?.listCertificateAssets?.templates || []).map(
		(t) => ({
			label: t.fileName,
			value: t.key,
		})
	);

	const fontOptions = (data?.listCertificateAssets?.fonts || []).map((f) => ({
		label: f.fileName,
		value: f.key,
	}));

	const configKeys = data?.listCertificateAssets?.configs || [];

	return (
		<div style={{ maxWidth: 1200, margin: '40px auto', padding: '0 20px' }}>
			<Card title="Certificate Generator">
				<FieldInspectorTab
					templateOptions={templateOptions}
					fontOptions={fontOptions}
					loadingAssets={loadingAssets}
					companyId={companyId}
					refetchAssets={refetchAssets}
					configKeys={configKeys}
				/>
			</Card>
		</div>
	);
}

export default CertificateGeneratorComponent;
